/* Hyperspeed-style PDP popup. Its content is NOT in the initial PDP HTML.
 * GET <localized page URL>?section_id=product-tab-modal-page renders only the
 * selected Shopify Page section in the customer's locale.
 * Dedicated popup state avoids collisions with the theme's global Terms popup.
 * The opened popup is moved to <body>, like native Halo popups, to avoid
 * PDP stacking contexts and custom high-z-index overlay styles.
 */
(function () {
  'use strict';
  if (window.__productTabPageModalReady) return;
  window.__productTabPageModalReady = true;

  var sectionName = 'product-tab-modal-page';
  var popupSelector = '.halo-product-tab-page-popup';
  var bodyClass = 'product-tab-page-popup-show';

  function getOpenPopup() {
    return document.querySelector(popupSelector + '.is-open');
  }

  // Popup triggers are now rendered directly by Liquid. Never rewrite links
  // inside the Custom Tab: merchants may include unrelated Page links there.

  // Reuse the theme's SVG ring, without showing visible loading text.
  // Keep the translated status announcement for screen-reader users.
  function setLoadingStatus(status, loading, label) {
    status.classList.toggle('is-loading', loading);
    var announcement = status.querySelector('[data-product-tab-page-status-text]');
    if (announcement) announcement.textContent = loading ? (label || '') : '';
  }

  function showError(popup, content, status) {
    content.replaceChildren();
    var message = document.createElement('p');
    message.textContent = popup.dataset.productTabPageError || 'Unable to load this page.';
    var paragraph = document.createElement('p');
    var link = document.createElement('a');
    link.textContent = popup.dataset.productTabPageOpen || 'Open the page';
    link.href = popup.dataset.productTabPageUrl || '#';
    paragraph.appendChild(link);
    content.appendChild(message);
    content.appendChild(paragraph);
    content.setAttribute('aria-busy', 'false');
    setLoadingStatus(status, false);
  }

  async function loadPage(popup) {
    if (popup.dataset.productTabPageLoaded === 'true') return;
    if (popup._productTabPageFetching) return popup._productTabPageFetching;

    var content = popup.querySelector('[data-product-tab-page-content]');
    var status = popup.querySelector('[data-product-tab-page-status]');
    if (!content || !status) return;
    content.replaceChildren();
    content.setAttribute('aria-busy', 'true');
    setLoadingStatus(status, true, popup.dataset.productTabPageLoading || 'Loading…');

    popup._productTabPageFetching = (async function () {
      try {
        var url = new URL(popup.dataset.productTabPageUrl, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Unexpected page origin');
        url.searchParams.set('section_id', sectionName);
        var response = await fetch(url.href, {
          credentials: 'same-origin',
          headers: { 'Accept': 'text/html' }
        });
        if (!response.ok) throw new Error('Section request failed: ' + response.status);
        var html = await response.text();
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var pageFragment = doc.querySelector('[data-product-tab-modal-page-content]');
        if (!pageFragment) throw new Error('Popup page section was not returned');
        content.innerHTML = pageFragment.innerHTML;
        content.setAttribute('aria-busy', 'false');
        setLoadingStatus(status, false);
        popup.dataset.productTabPageLoaded = 'true';
      } catch (error) {
        console.error('[Product tab popup] Unable to load selected Shopify Page:', error);
        showError(popup, content, status);
        // Failed loads are not cached: reopening the popup retries.
      } finally {
        popup._productTabPageFetching = null;
      }
    })();
    return popup._productTabPageFetching;
  }

  // Native Halo popups live directly under <body>. Product tabs do not:
  // they can inherit a transformed/positioned stacking context from the PDP.
  // Temporarily place the open popup under <body> so the shared backdrop
  // cannot cover the popup, regardless of the PDP container's z-index.
  function mountPopupAtBody(popup) {
    if (popup.parentNode === document.body) return;
    popup._productTabPageParent = popup.parentNode;
    popup._productTabPageNextSibling = popup.nextSibling;
    document.body.appendChild(popup);
  }

  function restorePopupToSection(popup) {
    var parent = popup._productTabPageParent;
    var nextSibling = popup._productTabPageNextSibling;
    if (!parent) return;
    if (parent.isConnected) {
      parent.insertBefore(popup, nextSibling && nextSibling.parentNode === parent ? nextSibling : null);
    } else {
      popup.remove();
    }
    popup._productTabPageParent = null;
    popup._productTabPageNextSibling = null;
  }

  function closePopup(popup, restoreFocus) {
    if (!popup || !popup.classList.contains('is-open')) return;
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    if (!getOpenPopup()) document.body.classList.remove(bodyClass);
    restorePopupToSection(popup);
    if (restoreFocus !== false && popup._productTabPageOpener && popup._productTabPageOpener.isConnected) {
      popup._productTabPageOpener.focus();
    }
    popup._productTabPageOpener = null;
  }

  function openPopup(popup, opener) {
    var other = getOpenPopup();
    if (other && other !== popup) closePopup(other, false);
    popup._productTabPageOpener = opener;
    mountPopupAtBody(popup);
    document.body.classList.add(bodyClass);
    popup.setAttribute('aria-hidden', 'false');
    popup.classList.add('is-open');
    var closeButton = popup.querySelector('[data-product-tab-page-close]');
    (closeButton || popup).focus();
    loadPage(popup);
  }

  document.addEventListener('click', function (event) {
    var target = event.target && event.target.closest ? event.target : event.target && event.target.parentElement;
    if (!target || !target.closest) return;
    var opener = target.closest('[data-product-tab-page-opener]');
    if (opener) {
      var popup = document.getElementById(opener.getAttribute('data-product-tab-page-opener'));
      if (!popup || !popup.matches(popupSelector)) return; // normal href fallback
      event.preventDefault();
      event.stopPropagation();
      openPopup(popup, opener);
      return;
    }
    var close = target.closest('[data-product-tab-page-close]');
    if (close) {
      var currentPopup = close.closest(popupSelector);
      if (currentPopup) {
        event.preventDefault();
        event.stopPropagation();
        closePopup(currentPopup);
      }
      return;
    }
    // Reuse Hyperspeed's existing shared background overlay.
    var active = getOpenPopup();
    if (active && target.closest('.background-overlay')) {
      event.preventDefault();
      event.stopPropagation();
      closePopup(active);
    }
  }, true);

  document.addEventListener('keydown', function (event) {
    var popup = getOpenPopup();
    if (!popup) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closePopup(popup);
    } else if (event.key === 'Tab') {
      var focusables = Array.from(popup.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(function (element) { return element.getClientRects().length > 0; });
      if (!focusables.length) { event.preventDefault(); popup.focus(); return; }
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && (document.activeElement === first || !popup.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !popup.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    }
  }, true);

  // Delegated click handling above works for first render and Theme Editor
  // section reloads alike; no link mutation or re-initialization is required.
  document.addEventListener('shopify:section:unload', function (event) {
    var open = getOpenPopup();
    if (open && (event.target.contains(open) ||
      (open._productTabPageParent && event.target.contains(open._productTabPageParent)))) {
      closePopup(open, false);
    }
  });
})();
